import { useEffect, useState } from 'react';

import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Divider } from 'primereact/divider';
import { TenantCompanyService as Service } from './TenantCompanyService';
import { CityService } from './CityService';
import { TaxAuthorityService } from './TaxAuthorityService';
import { AppStorage } from '../../../../service/UserStorage';
import EventEmitter from '../../../../utils/EventEmitter';
import { TenantCompanyHistoryService } from './TenantCompanyHistoryService';

import { useForm } from '../../../../shared/hooks/Form';
import { useDataTable } from '../../../../shared/hooks/DataTable';
import { ToastService } from '../../../../AppToast';
import { useTranslation } from '../../../../shared/language';

export const Detail = () => {
  const { t } = useTranslation();

  const [submitting, setSubmitting] = useState(false);
  const [displayLogs, setDisplayLogs] = useState(false);
  const [originData, setOriginData] = useState(null);
  const [hidenIdentityCode, setHidenIdentityCode] = useState(false);
  const [useIdentityCard, setUseIdentityCard] = useState(false);
  const [cities, setCities] = useState([]);
  const [taxAuthorities, setTaxAuthorities] = useState([]);

  useEffect(() => {
    CityService.getList({ maxResultCount: 1000 }).then(({ items }) => {
      setCities(items);
    });
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (originData && originData.cityCode && cities && cities.length) {
      cities.forEach(city => {
        if (city.code === originData.cityCode) {
          originData.city = city;
          setValue(originData);
        }
      });
    }
  }, [originData, cities]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (originData && originData.taxAuthoritiesId && taxAuthorities && taxAuthorities.length) {
      taxAuthorities.forEach(taxAuthority => {
        if (taxAuthority.id === originData.taxAuthoritiesId) {
          originData.taxAuthoritiesId = taxAuthority;
          setValue(originData);
          setOriginData(null);
        }
      });
    }
  }, [originData, taxAuthorities]); // eslint-disable-line react-hooks/exhaustive-deps

  const { render: renderForm, setValue, valid: formValid, getValue } = useForm({
    fields: [
      { field: 'taxCode', header: t('common.taxCode'), disabled: true, InputTextProps: { maxLength: 50 }, className: 'md:col-2' },
      { field: 'identityCode', header: t('system.identityCode'), disabled: true, InputTextProps: { maxLength: 50 }, className: 'md:col-2' },
      { field: 'name', header: t('common.entityName'), required: true, InputTextProps: { maxLength: 400 }, className: 'md:col-8' },

      { field: 'address', header: t('common.address'), required: true, InputTextProps: { maxLength: 500 }, className: 'md:col-9' },
      { field: 'province', header: t('system.province'), required: true, InputTextProps: { maxLength: 50 }, className: 'md:col-3' },

      { field: 'bankAccount', header: t('system.bankAccount'), InputTextProps: { maxLength: 50 }, className: 'md:col-3' },
      { field: 'bankName', header: t('system.bankName'), InputTextProps: { maxLength: 400 }, className: 'md:col-6' },
      { field: 'paymentCode', header: t('system.paymentIntegrationCode'), InputTextProps: { maxLength: 400 }, className: 'md:col-3' },

      { field: '', type: 'custom', className: 'md:col-12', body: <Divider className='m-0'><p className="text-base font-bold">{t('system.legalRepresentativeSection')}</p></Divider> },

      { field: 'delegate', header: t('system.legalRepresentative'), InputTextProps: { maxLength: 400 }, className: 'md:col-3' },
      { field: 'position', header: t('common.position'), InputTextProps: { maxLength: 50 }, className: 'md:col-3' },
      { field: 'legalRepPhone', header: t('system.legalRepresentativePhone'), InputTextProps: { maxLength: 20 }, className: 'md:col-3' },
      { field: 'legalRepDob', header: t('common.dateOfBirth'), type: 'Calendar', className: 'md:col-3' },

      { field: 'legalRepPassport', header: t('common.passportLabel'), InputTextProps: { maxLength: 20 }, className: 'md:col-3' },
      { field: 'identityCard', header: t('common.citizenId'), InputTextProps: { maxLength: 12 }, required: useIdentityCard, className: 'md:col-2', },
      { field: 'useIdentityCard', header: t('system.useCitizenId'), type: 'Checkbox', required: false, className: 'md:col-4 flex align-items-end' },
      {
        field: 'legalRepGender', header: t('common.gender'), type: 'RadioButton', RadioButtonProps: {
          RadioButtons: [
            { className: 'md:col-6', value: 1, label: t('common.male') },
            { className: 'md:col-6', value: 0, label: t('common.female') }
          ]
        },
        className: 'md:col-3',
      },
      { field: '', type: 'custom', className: 'md:col-12', body: <Divider className='m-0'><p className="text-base font-bold">{t('system.contactSection')}</p></Divider> },

      { field: 'email', header: 'Email', type: 'email', InputTextProps: { maxLength: 50 }, className: 'md:col-6' },
      { field: 'tel', header: t('common.phone'), InputTextProps: { maxLength: 15 }, className: 'md:col-6' },
      { field: 'webSite', header: 'WebSite', InputTextProps: { maxLength: 250 }, className: 'md:col-6' },
      { field: 'fax', header: 'Fax', InputTextProps: { maxLength: 15 }, className: 'md:col-6' },
      { field: '', type: 'custom', className: 'md:col-12', body: <Divider className='m-0'><p className="text-base font-bold">{t('system.taxAuthoritySection')}</p></Divider> },

      { field: 'city', header: t('system.provincialTaxAuthority'), type: 'Dropdown', DropdownProps: { options: cities, optionLabel: 'name', filter: true, resetFilterOnHide: true }, className: 'md:col-6' },
      { field: 'taxAuthoritiesId', header: t('system.managingTaxAuthority'), type: 'Dropdown', DropdownProps: { options: taxAuthorities, optionLabel: 'nameDisplay', filter: true, resetFilterOnHide: true }, className: 'md:col-6' },
      { field: 'agentInfo', header: t('system.agentInfo'), disabled: true, required: false, InputTextProps: { maxLength: 500 }, className: 'md:col-12' },
    ]
  });

  const { city } = getValue();
  useEffect(() => {
    if (city && city.code) {
      TaxAuthorityService.getList({ cityCode: city.code, maxResultCount: 1000 }).then(({ items }) => {
        setTaxAuthorities(items);
      });
    }
  }, [city]);

  useEffect(() => {
    setUseIdentityCard(getValue().useIdentityCard);
  }, [getValue().useIdentityCard]);

  const load = () => {
    Service.get(AppStorage.get('tenant')).then((data) => {
      AppStorage.set('isHouseholdBusiness', data.isHouseholdBusiness === true ? 'true' : 'false');
      AppStorage.set('tenantInfo', JSON.stringify(data));
      EventEmitter.emit('changeTenantInfo', data);
      setOriginData(data);
      setValue(data);
    });
  }

  const handleSubmit = () => {
    if (!submitting && formValid()) {
      setSubmitting(true);

      const data = getValue();

      if (data.city) {
        data.cityCode = data.city.code;
        data.cityName = data.city.name;
        delete data.city;
      }
      if (data.taxAuthoritiesId) {
        data.taxAuthorityCode = data.taxAuthoritiesId.code;
        data.taxAuthorityName = data.taxAuthoritiesId.name;
        data.taxAuthoritiesId = data.taxAuthoritiesId.id;
        delete data.taxAuthority;
      }

      Service.update(AppStorage.get('tenant'), data).then(() => {
        ToastService.success();
        load();
      }).finally(() => {
        setSubmitting(false);
      });
    }
  }

  const { render: renderDataTable, reloadLazyData } = useDataTable({
    header: t('system.changeHistory'),
    dataKey: 'id',
    columns: [
      { field: 'creatorName', header: t('system.modifiedBy'), width: 160 },
      { field: 'lastModificationTime', header: t('system.modifiedDate'), width: 180, dataType: 'date' },
      { field: 'name', header: t('system.data'), minWidth: 200 }
    ],
    indexColumnWidth: 40,
    selectionMode: 'single',
    getList: (params) => {
      return TenantCompanyHistoryService.getList(params);
    }
  });

  const showLogsDialog = () => {
    reloadLazyData();
    setDisplayLogs(true);
  }

  return (
    <div className="surface-card p-5" style={{ width: '1300px', margin: '0 auto' }}>
      {renderForm()}

      <div className="flex justify-content-center">
        <Button label={t('system.viewChangeHistory')} icon="pi pi-clock" onClick={() => showLogsDialog()} className="p-button-text p-button-sm" />
        <Button label={t('common.save')} icon="pi pi-check" onClick={handleSubmit} loading={submitting} className="p-button-sm" />
      </div>

      <Dialog header={t('system.changeHistoryInfo')} visible={displayLogs} style={{ width: '80rem' }} position="top" onHide={() => setDisplayLogs(false)}>
        {renderDataTable()}

        <div className="flex justify-content-end mt-3">
          <Button label={t('common.close')} icon="pi pi-times" onClick={() => setDisplayLogs(false)} className="p-button-text p-button-sm" />
        </div>
      </Dialog>
    </div>
  )
}
